import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiMessageSquare } from 'react-icons/fi';
import { API_URL } from '../../Api/api';
import { getAuthToken } from '../../Api/authToken';
import './AdminPages.css';

const ChatConfiguration = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showWebChat, setShowWebChat] = useState(false);
  const [mailMinutes, setMailMinutes] = useState(10);
  const [greeting, setGreeting] = useState('Hello! How can we help you today?');
  const [propertyPrompt, setPropertyPrompt] = useState('Do you like this property?');
  const [propertyWait, setPropertyWait] = useState('Chat wait some seconds.');
  const [propertyYes, setPropertyYes] = useState('Yes');
  const [propertyYesMessage, setPropertyYesMessage] = useState('Yes, I like this property.');
  const [propertyNo, setPropertyNo] = useState('No');
  const [suggestionsText, setSuggestionsText] = useState('');

  const headers = () => ({ Authorization: `Bearer ${getAuthToken()}` });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/chat/config`, { headers: headers() });
      setShowWebChat(!!res.data?.show_web_chat);
      setMailMinutes(Number(res.data?.mail_time_minutes) || 10);
      setGreeting(res.data?.greeting || 'Hello! How can we help you today?');
      setPropertyPrompt(res.data?.property_prompt || 'Do you like this property?');
      setPropertyWait(res.data?.property_wait || 'Chat wait some seconds.');
      setPropertyYes(res.data?.property_yes || 'Yes');
      setPropertyYesMessage(res.data?.property_yes_message || 'Yes, I like this property.');
      setPropertyNo(res.data?.property_no || 'No');
      setSuggestionsText(Array.isArray(res.data?.suggestions) ? res.data.suggestions.join('\n') : '');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to load chat settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    setSaving(true);
    try {
      const res = await axios.put(`${API_URL}/admin/chat/config`, patch, { headers: headers() });
      setShowWebChat(!!res.data?.show_web_chat);
      setMailMinutes(Number(res.data?.mail_time_minutes) || 10);
      setGreeting(res.data?.greeting || 'Hello! How can we help you today?');
      setPropertyPrompt(res.data?.property_prompt || 'Do you like this property?');
      setPropertyWait(res.data?.property_wait || 'Chat wait some seconds.');
      setPropertyYes(res.data?.property_yes || 'Yes');
      setPropertyYesMessage(res.data?.property_yes_message || 'Yes, I like this property.');
      setPropertyNo(res.data?.property_no || 'No');
      setSuggestionsText(Array.isArray(res.data?.suggestions) ? res.data.suggestions.join('\n') : '');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save chat settings');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const toggleChat = async (enabled) => {
    const prev = showWebChat;
    setShowWebChat(enabled);
    const ok = await save({ show_web_chat: enabled, mail_time_minutes: mailMinutes });
    if (!ok) setShowWebChat(prev);
  };

  const saveMailTime = async () => {
    const minutes = Math.min(1440, Math.max(1, Number(mailMinutes) || 10));
    setMailMinutes(minutes);
    const ok = await save({ show_web_chat: showWebChat, mail_time_minutes: minutes });
    if (ok) toast.success(`Chat emails will send after ${minutes} minutes`);
  };

  const saveGreeting = async () => {
    const ok = await save({ greeting: greeting.trim() });
    if (ok) toast.success('Greeting saved');
  };

  const savePropertyPrompt = async () => {
    const ok = await save({
      property_prompt: propertyPrompt.trim(),
      property_wait: propertyWait.trim(),
      property_yes: propertyYes.trim(),
      property_yes_message: propertyYesMessage.trim(),
      property_no: propertyNo.trim(),
    });
    if (ok) toast.success('Property chat text saved');
  };

  const saveSuggestions = async () => {
    const suggestions = suggestionsText.split('\n').map((line) => line.trim()).filter(Boolean);
    const ok = await save({ suggestions });
    if (ok) toast.success('Suggestions saved');
  };

  return (
    <div className="admin-page">
      <div className="page-header">
        <h1><FiMessageSquare style={{ marginRight: 8 }} />Chat Configuration</h1>
        <p>Choose when the website chat is visible and when email alerts are sent.</p>
      </div>

      <div className="admin-card">
        {loading ? (
          <div className="skeleton" style={{ height: 160, borderRadius: 8 }} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
              <div>
                <h3 style={{ margin: '0 0 6px' }}>Show Web Chat</h3>
                <p style={{ margin: 0, color: 'var(--admin-text-muted)', maxWidth: 560 }}>
                  When enabled, every page shows a chat icon instead of the language icon. When disabled, the language icon stays in place.
                </p>
              </div>
              <label className="toggle-label" style={{ flexShrink: 0 }}>
                <span>{showWebChat ? 'Enabled' : 'Disabled'}</span>
                <input
                  type="checkbox"
                  checked={showWebChat}
                  disabled={saving}
                  onChange={(e) => toggleChat(e.target.checked)}
                />
                <div className="toggle-switch" />
              </label>
            </div>

            <div>
              <h3 style={{ margin: '0 0 6px' }}>Chat Mail Time</h3>
              <p style={{ margin: '0 0 10px', color: 'var(--admin-text-muted)', maxWidth: 560 }}>
                A new chat emails both sides right away. If nobody replies, another email is sent after this many minutes.
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <input
                  type="number"
                  min="1"
                  max="1440"
                  value={mailMinutes}
                  onChange={(e) => setMailMinutes(e.target.value)}
                  onBlur={saveMailTime}
                  style={{ width: 96, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--admin-border)' }}
                />
                <span style={{ color: 'var(--admin-text-muted)' }}>minutes</span>
              </div>
            </div>

            <div>
              <h3 style={{ margin: '0 0 6px' }}>Opening greeting</h3>
              <p style={{ margin: '0 0 10px', color: 'var(--admin-text-muted)', maxWidth: 560 }}>
                Shown automatically the first time someone opens the chat.
              </p>
              <textarea
                value={greeting}
                onChange={(e) => setGreeting(e.target.value)}
                onBlur={saveGreeting}
                rows={3}
                style={{ width: '100%', maxWidth: 560, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--admin-border)', resize: 'vertical' }}
              />
            </div>

            <div>
              <h3 style={{ margin: '0 0 6px' }}>Property page chat</h3>
              <p style={{ margin: '0 0 10px', color: 'var(--admin-text-muted)', maxWidth: 560 }}>
                After someone opens the chat on a property, the wait line shows until the question is active. Yes and No are the reply buttons.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 560 }}>
                <input
                  value={propertyWait}
                  onChange={(e) => setPropertyWait(e.target.value)}
                  onBlur={savePropertyPrompt}
                  aria-label="Property wait text"
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--admin-border)' }}
                />
                <input
                  value={propertyPrompt}
                  onChange={(e) => setPropertyPrompt(e.target.value)}
                  onBlur={savePropertyPrompt}
                  aria-label="Property prompt"
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--admin-border)' }}
                />
                <input
                  value={propertyYesMessage}
                  onChange={(e) => setPropertyYesMessage(e.target.value)}
                  onBlur={savePropertyPrompt}
                  aria-label="Message sent for Yes"
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--admin-border)' }}
                />
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    value={propertyYes}
                    onChange={(e) => setPropertyYes(e.target.value)}
                    onBlur={savePropertyPrompt}
                    aria-label="Yes button"
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--admin-border)' }}
                  />
                  <input
                    value={propertyNo}
                    onChange={(e) => setPropertyNo(e.target.value)}
                    onBlur={savePropertyPrompt}
                    aria-label="No button"
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--admin-border)' }}
                  />
                </div>
              </div>
            </div>

            <div>
              <h3 style={{ margin: '0 0 6px' }}>Message suggestions</h3>
              <p style={{ margin: '0 0 10px', color: 'var(--admin-text-muted)', maxWidth: 560 }}>
                One suggestion per line. These appear while a visitor is typing.
              </p>
              <textarea
                value={suggestionsText}
                onChange={(e) => setSuggestionsText(e.target.value)}
                onBlur={saveSuggestions}
                rows={6}
                placeholder={'I want more details about this property\nIs this property still available?'}
                style={{ width: '100%', maxWidth: 560, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--admin-border)', resize: 'vertical' }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatConfiguration;
